import { useList, useGo, useDelete, useCustomMutation, useApiUrl } from "@refinedev/core";
import { useMemo, useState } from "react";
import { notify } from '@/lib/notify';
import {
  Eye,
  Users as UsersIcon,
  History,
  Trash2,
  Lock,
  Unlock,
  Tag,
  CheckSquare,
  Mail,
  MailCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { FormHeader } from "@/components/forms";
import { useConfirm } from "@/hooks/use-confirm";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";
import { BLOCK_REASON_LABELS, getUserAccountStatus, type BlockUserPayload, type User, type UserTag } from "./types";
import { BlockUserDialog } from "./components/BlockUserDialog";
import { BulkTagsDialog } from "@/components/bulk-tags-dialog";
import { getFlagEmoji, type Language } from "@/resources/languages/types";
import { codeToFlagEmoji, type CountryItem } from "@/resources/countries/types";
import { SegmentFilterSheet } from "@/components/audience-segment";
import type { SegmentDefinition } from "@masters-club/validation";
import { apiClient } from "@/providers/api-client";
import {
  UsersFiltersSheet,
  type UsersFiltersValue,
} from "./components/UsersFiltersSheet";

interface TagOption {
  id: number;
  name: string;
  color: string;
}

const EMPTY_SEGMENT: SegmentDefinition = { groupsOp: 'AND', groups: [] };

const ACQUISITION_OPTIONS: { value: string; label: string }[] = [
  { value: 'direct', label: 'Прямой переход' },
  { value: 'bot', label: 'Telegram-бот' },
  { value: 'referral', label: 'Реферальная программа' },
  { value: 'utm_source', label: 'UTM-источник' },
  { value: 'utm_medium', label: 'UTM-канал' },
  { value: 'utm_campaign', label: 'UTM-кампания' },
];

function isSegmentEmpty(def: SegmentDefinition): boolean {
  return def.groups.length === 0;
}

interface SavedSegment {
  id: number;
  name: string;
  definition: SegmentDefinition;
}

interface AudienceIdsResponse {
  ids: number[];
  total: number;
}

interface SegmentListResponse {
  items: SavedSegment[];
  total: number;
}

/**
 * Inline badges for user tags — max 3 visible, rest as "+N"
 */
function TagsBadges({ tags }: { tags: UserTag[] }) {
  if (tags.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const visible = tags.slice(0, 3);
  const hidden = tags.length - visible.length;

  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-white"
          style={{ backgroundColor: tag.color }}
        >
          {tag.name}
        </span>
      ))}
      {hidden > 0 && (
        <Badge variant="outline" className="text-xs">
          +{hidden}
        </Badge>
      )}
    </div>
  );
}

/**
 * Users list page with DataTable
 */
export function UsersList() {
  useScrollRestoration();

  const go = useGo();
  const apiUrl = useApiUrl();
  const [page, setPage] = usePersistedState('users:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('users:sort', { field: "createdAt", order: "desc" });
  const [search, setSearch] = usePersistedState('users:search', "");
  const [persistedFilters, setFiltersValue] = usePersistedState<UsersFiltersValue>('users:filters', {
    statusFilter: 'all',
    tagFilter: '',
    languageFilter: '',
    countryFilter: '',
    currencyFilter: '',
    acquisitionFilter: '',
    hasEmailFilter: 'all',
    emailVerifiedFilter: 'all',
    createdAtFrom: undefined,
    createdAtTo: undefined,
    lastPaymentFrom: undefined,
    lastPaymentTo: undefined,
  });
  // Date-поля переживают JSON-сериализацию sessionStorage как ISO-строки —
  // ревайвим их обратно в Date, чтобы downstream-код (.toISOString()) работал.
  const filtersValue = useMemo<UsersFiltersValue>(() => {
    const reviveDate = (value: Date | string | undefined): Date | undefined => {
      if (value === undefined) {
        return undefined;
      }
      return value instanceof Date ? value : new Date(value);
    };
    return {
      ...persistedFilters,
      createdAtFrom: reviveDate(persistedFilters.createdAtFrom),
      createdAtTo: reviveDate(persistedFilters.createdAtTo),
      lastPaymentFrom: reviveDate(persistedFilters.lastPaymentFrom),
      lastPaymentTo: reviveDate(persistedFilters.lastPaymentTo),
    };
  }, [persistedFilters]);
  const {
    statusFilter,
    tagFilter,
    languageFilter,
    countryFilter,
    currencyFilter,
    acquisitionFilter,
    hasEmailFilter,
    emailVerifiedFilter,
    createdAtFrom,
    createdAtTo,
    lastPaymentFrom,
    lastPaymentTo,
  } = filtersValue;
  const pageSize = 15;

  // Сегментный фильтр — применяется вместе со старыми фильтрами (AND)
  const [activeSegment, setActiveSegment] = useState<SegmentDefinition>(EMPTY_SEGMENT);

  // Сохранение сегмента
  const [saveSegmentName, setSaveSegmentName] = useState('');
  const [isSavingSegment, setIsSavingSegment] = useState(false);
  // Загруженные сохранённые сегменты
  const [savedSegments, setSavedSegments] = useState<SavedSegment[]>([]);
  const [isLoadingSegments, setIsLoadingSegments] = useState(false);

  // Bulk-состояние для «Выбрать всё по фильтру»
  const [bulkAllIds, setBulkAllIds] = useState<number[] | null>(null);
  const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);

  // Загружаем все активные теги для фильтра
  const tagsResult = useList<TagOption>({
    resource: 'tags',
    pagination: { mode: 'off' },
    queryOptions: { staleTime: 60_000 },
  });
  const availableTags = tagsResult.result.data ?? [];

  // Загружаем доступные языки для фильтра по предпочитаемому языку
  const languagesResult = useList<Language>({
    resource: 'languages',
    pagination: { mode: 'off' },
    queryOptions: { staleTime: 60_000 },
  });
  const availableLanguages = languagesResult.result.data ?? [];

  // Загружаем страны для фильтра по стране пользователя
  const countriesResult = useList<CountryItem>({
    resource: 'admin/countries',
    pagination: { mode: 'off' },
    sorters: [{ field: 'sortOrder', order: 'asc' }],
    queryOptions: { staleTime: 60_000 },
  });
  // Мемоизуем по reference из react-query (data не меняется без рефетча),
  // чтобы useMemo ниже не пересчитывался каждый рендер.
  const availableCountries = useMemo(
    () => countriesResult.result.data ?? [],
    [countriesResult.result.data],
  );

  // Список валют, которые встречаются у юзеров — собираем из allowedCurrencies стран.
  // Этого достаточно для фильтра, не делая отдельного эндпойнта.
  const availableCurrencies = useMemo(() => {
    const set = new Set<string>();
    for (const c of availableCountries) {
      for (const cur of c.allowedCurrencies ?? []) {
        if (cur) set.add(cur);
      }
    }
    // Базовые валюты — всегда показываем даже если их нет в странах.
    ['USD', 'UZS', 'RUB', 'EUR'].forEach((c) => set.add(c));
    return Array.from(set).sort();
  }, [availableCountries]);

  // Каналы привлечения: стандартный набор + utm_*. UTM-источники — отдельный фильтр выше по плану.
  const acquisitionOptions = ACQUISITION_OPTIONS;

  const segmentFilter = useMemo(() => {
    if (isSegmentEmpty(activeSegment)) return '';
    return JSON.stringify(activeSegment);
  }, [activeSegment]);

  const listResult = useList<User>({
    resource: "users",
    pagination: { currentPage: page, pageSize, mode: "server" },
    filters: useMemo(() => {
      const f: { field: string; operator: 'contains' | 'eq'; value: string }[] = [];
      if (search) {
        f.push({ field: 'search', operator: 'contains', value: search });
      }
      if (statusFilter !== 'all') {
        f.push({ field: 'status', operator: 'eq', value: statusFilter });
      }
      if (tagFilter) {
        // backend принимает ?tagIds=1,2,3 (comma-separated)
        f.push({ field: 'tagIds', operator: 'eq', value: tagFilter });
      }
      if (languageFilter) {
        // backend принимает ?languagePreference=<code>
        f.push({ field: 'languagePreference', operator: 'eq', value: languageFilter });
      }
      if (countryFilter) {
        // backend принимает ?countryId=<id>
        f.push({ field: 'countryId', operator: 'eq', value: countryFilter });
      }
      if (currencyFilter) {
        // backend принимает ?currencyPreference=<code>
        f.push({ field: 'currencyPreference', operator: 'eq', value: currencyFilter });
      }
      if (acquisitionFilter) {
        // backend принимает ?acquisitionChannel=<value>
        f.push({ field: 'acquisitionChannel', operator: 'eq', value: acquisitionFilter });
      }
      if (hasEmailFilter !== 'all') {
        f.push({ field: 'hasEmail', operator: 'eq', value: hasEmailFilter });
      }
      if (emailVerifiedFilter !== 'all') {
        f.push({ field: 'emailVerified', operator: 'eq', value: emailVerifiedFilter });
      }
      if (createdAtFrom) {
        f.push({ field: 'createdAtFrom', operator: 'eq', value: createdAtFrom.toISOString() });
      }
      if (createdAtTo) {
        // Включающая граница «по день»: переносим on 23:59:59 локального дня.
        const end = new Date(createdAtTo);
        end.setHours(23, 59, 59, 999);
        f.push({ field: 'createdAtTo', operator: 'eq', value: end.toISOString() });
      }
      if (lastPaymentFrom) {
        f.push({
          field: 'lastPaymentFrom',
          operator: 'eq',
          value: lastPaymentFrom.toISOString(),
        });
      }
      if (lastPaymentTo) {
        const end = new Date(lastPaymentTo);
        end.setHours(23, 59, 59, 999);
        f.push({ field: 'lastPaymentTo', operator: 'eq', value: end.toISOString() });
      }
      if (segmentFilter) {
        // backend принимает ?segment=<JSON>
        f.push({ field: 'segment', operator: 'eq', value: segmentFilter });
      }
      return f;
    }, [
      search,
      statusFilter,
      tagFilter,
      languageFilter,
      countryFilter,
      currencyFilter,
      acquisitionFilter,
      hasEmailFilter,
      emailVerifiedFilter,
      createdAtFrom,
      createdAtTo,
      lastPaymentFrom,
      lastPaymentTo,
      segmentFilter,
    ]),
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const data = listResult.result.data ?? [];
  const total = listResult.result.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const { mutate: deleteUser } = useDelete();
  const { mutate: blockMutate } = useCustomMutation();
  const { mutate: unblockMutate } = useCustomMutation();
  const { confirm, ConfirmationDialog } = useConfirm();

  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockTargets, setBlockTargets] = useState<User[]>([]);

  // Tags bulk dialog state
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [tagsDialogUserIds, setTagsDialogUserIds] = useState<number[]>([]);
  const [tagsDialogLabel, setTagsDialogLabel] = useState('');

  const buildUserName = (u: User): string =>
    [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || `#${u.id}`;

  const handleDelete = async (items: User[]) => {
    if (items.length === 0) {return;}

    const confirmed = await confirm({
      title:
        items.length === 1
          ? `Удалить пользователя «${buildUserName(items[0])}»?`
          : `Удалить ${items.length} пользователей?`,
      description:
        "Это действие необратимо. Все активные доступы будут отозваны, " +
        "пользователь будет выкинут из всех сессий, а его профиль скрыт от админ-панели.",
      variant: "destructive",
      confirmText: "Удалить",
      cancelText: "Отмена",
    });

    if (!confirmed) {return;}

    for (const item of items) {
      deleteUser(
        { resource: "users", id: item.id },
        {
          onSuccess: () => {
            notify.success(
              items.length === 1
                ? "Пользователь удалён"
                : `Удалено пользователей: ${items.length}`,
            );
            void listResult.query.refetch();
          },
          onError: (error) => {
            const message =
              error instanceof Error ? error.message : "Не удалось удалить пользователя";
            notify.error(message);
          },
        },
      );
    }
  };

  const handleOpenBlock = (items: User[]) => {
    const blockable = items.filter((u) => !u.blockedAt && !u.deletedAt);
    if (blockable.length === 0) {
      notify.error('Среди выбранных нет пользователей, которых можно заблокировать');
      return;
    }
    if (blockable.length < items.length) {
      notify.warning(
        `Будут заблокированы ${blockable.length} из ${items.length} (остальные уже заблокированы или удалены)`,
      );
    }
    setBlockTargets(blockable);
    setBlockDialogOpen(true);
  };

  const handleConfirmBlock = async (payload: BlockUserPayload): Promise<void> => {
    if (blockTargets.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      blockTargets.map(
        (u) =>
          new Promise<void>((resolve, reject) => {
            blockMutate(
              {
                url: `${apiUrl}/users/${u.id}/block`,
                method: 'post',
                values: payload,
              },
              {
                onSuccess: () => resolve(),
                onError: (error) => reject(error),
              },
            );
          }),
      ),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - succeeded;

    if (succeeded > 0) {
      notify.success(
        succeeded === 1 ? 'Пользователь заблокирован' : `Заблокировано: ${succeeded}`,
      );
      void listResult.query.refetch();
    }
    if (failed > 0) {
      const firstError = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      const errMessage =
        firstError && firstError.reason instanceof Error
          ? firstError.reason.message
          : 'Не удалось заблокировать часть пользователей';
      notify.error(`Ошибок: ${failed}. ${errMessage}`);
      if (succeeded === 0) {
        throw firstError?.reason instanceof Error
          ? firstError.reason
          : new Error(errMessage);
      }
    }
  };

  const handleUnblock = async (items: User[]): Promise<void> => {
    const unblockable = items.filter((u) => u.blockedAt && !u.deletedAt);
    if (unblockable.length === 0) {
      notify.error('Среди выбранных нет заблокированных пользователей');
      return;
    }

    const confirmed = await confirm({
      title:
        unblockable.length === 1
          ? `Разблокировать пользователя «${buildUserName(unblockable[0])}»?`
          : `Разблокировать ${unblockable.length} пользователей?`,
      description: 'Пользователи снова смогут пользоваться ботом и платформой.',
      confirmText: 'Разблокировать',
      cancelText: 'Отмена',
    });
    if (!confirmed) { return; }

    let succeeded = 0;
    let failed = 0;
    let firstErrorMessage: string | null = null;

    await Promise.all(
      unblockable.map(
        (u) =>
          new Promise<void>((resolve) => {
            unblockMutate(
              {
                url: `${apiUrl}/users/${u.id}/unblock`,
                method: 'post',
                values: {},
              },
              {
                onSuccess: () => {
                  succeeded++;
                  resolve();
                },
                onError: (error) => {
                  failed++;
                  if (!firstErrorMessage) {
                    firstErrorMessage =
                      error instanceof Error
                        ? error.message
                        : 'Не удалось разблокировать пользователя';
                  }
                  resolve();
                },
              },
            );
          }),
      ),
    );

    if (succeeded > 0) {
      notify.success(
        succeeded === 1 ? 'Пользователь разблокирован' : `Разблокировано: ${succeeded}`,
      );
      void listResult.query.refetch();
    }
    if (failed > 0) {
      notify.error(`Ошибок: ${failed}. ${firstErrorMessage ?? ''}`);
    }
  };

  // ─── Сегментный фильтр ────────────────────────────────────────────────────

  const loadSavedSegments = async () => {
    setIsLoadingSegments(true);
    try {
      const result = await apiClient.get<SegmentListResponse>('/audience/segments?limit=50');
      setSavedSegments(result.items);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось загрузить сегменты';
      notify.error(message);
    } finally {
      setIsLoadingSegments(false);
    }
  };

  const handleSaveSegment = async (definition: SegmentDefinition) => {
    if (!saveSegmentName.trim()) {
      notify.error('Введите название сегмента');
      return;
    }
    setIsSavingSegment(true);
    try {
      await apiClient.post('/audience/segments', {
        name: saveSegmentName.trim(),
        mode: 'DYNAMIC',
        definition,
      });
      notify.success('Сегмент сохранён');
      setSaveSegmentName('');
      await loadSavedSegments();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось сохранить сегмент';
      notify.error(message);
    } finally {
      setIsSavingSegment(false);
    }
  };

  // ─── «Выбрать всё по фильтру» ────────────────────────────────────────────

  const handleSelectAllByFilter = async () => {
    setIsLoadingAllIds(true);
    try {
      const body: {
        definition?: SegmentDefinition;
        includeUserIds?: number[];
      } = {};

      // Используем сегмент как основу definition
      if (!isSegmentEmpty(activeSegment)) {
        body.definition = activeSegment;
      } else {
        // Пустой сегмент = вся аудитория (передаём пустой definition)
        body.definition = EMPTY_SEGMENT;
      }

      const result = await apiClient.post<AudienceIdsResponse>('/audience/ids', body);
      setBulkAllIds(result.ids);
      notify.success(`Выбрано ${result.total} пользователей по фильтру`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не удалось получить список ID';
      notify.error(message);
    } finally {
      setIsLoadingAllIds(false);
    }
  };

  // Формируем User[] из bulkAllIds для bulk-диалогов (только id нужен для тегов/блокировки)
  const buildUsersFromIds = (ids: number[]): User[] => {
    const now = new Date().toISOString();
    return ids.map((id) => ({
      id,
      telegramId: '',
      firstName: null,
      lastName: null,
      username: null,
      phone: null,
      createdAt: now,
      updatedAt: now,
      deletedAt: null,
      blockedAt: null,
      blockedBy: null,
      blockReason: null,
      blockReasonCode: null,
      tags: [],
      subscriptions: [],
      accessibleCourses: [],
      accessibleChannels: [],
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      utmTerm: null,
      utmContent: null,
      email: null,
      isEmailVerified: false,
      currencyPreference: null,
      timezone: null,
      isActive: true,
      countryId: null,
      lastCountry: null,
      country: null,
      totalSpentBase: 0,
      purchaseCount: 0,
      firstPaymentAt: null,
      lastPaymentAt: null,
      acquisitionChannel: null,
    }));
  };

  const handleBulkTagsByFilter = () => {
    if (!bulkAllIds) {return;}
    setTagsDialogUserIds(bulkAllIds);
    setTagsDialogLabel(`${bulkAllIds.length} пользователей (все по фильтру)`);
    setTagsDialogOpen(true);
  };

  const handleBulkBlockByFilter = () => {
    if (!bulkAllIds) {return;}
    const users = buildUsersFromIds(bulkAllIds);
    setBlockTargets(users);
    setBlockDialogOpen(true);
  };

  // ─── Вспомогательные функции ─────────────────────────────────────────────

  const getSubscriptionBadge = (status: string | undefined) => {
    switch (status) {
      case "active":
        return (
          <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
            Активна
          </Badge>
        );
      case "expired":
        return <Badge variant="secondary">Истекла</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Отменена</Badge>;
      default:
        return <Badge variant="outline">Нет подписки</Badge>;
    }
  };

  const getAccountStatusBadge = (user: User): React.ReactNode => {
    const status = getUserAccountStatus(user);
    if (status === 'deleted') {
      return <Badge variant="outline">Удалён</Badge>;
    }
    if (status === 'blocked') {
      const reasonLabel = user.blockReasonCode
        ? BLOCK_REASON_LABELS[user.blockReasonCode]
        : null;
      const tooltipText = [reasonLabel, user.blockReason].filter(Boolean).join(' — ') ||
        'Причина не указана';
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="destructive" className="cursor-help">
                Заблокирован
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs whitespace-pre-wrap">
              {tooltipText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return (
      <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">Активен</Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatMoney = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '—';
    const num = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(num) || num === 0) return '—';
    return new Intl.NumberFormat('ru-RU', {
      maximumFractionDigits: 0,
    }).format(num);
  };

  const ACQUISITION_LABELS: Record<string, string> = {
    direct: 'Прямой',
    bot: 'Бот',
    referral: 'Реферал',
    utm_source: 'UTM',
    utm_medium: 'UTM',
    utm_campaign: 'UTM',
  };

  const columns: Column<User>[] = [
    {
      key: "id",
      header: "ID",
      width: "70px",
      sortable: true,
    },
    {
      key: "firstName",
      header: "Имя",
      sortable: true,
      render: (_, user) => {
        const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
        return (
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <UsersIcon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="font-medium">{name || "—"}</div>
              {user.username && (
                <div className="text-xs text-muted-foreground">
                  @{user.username}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) => {
        if (!user.email) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 max-w-[220px]">
                  {user.isEmailVerified ? (
                    <MailCheck className="h-3.5 w-3.5 shrink-0 text-green-500" />
                  ) : (
                    <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate text-sm">{user.email}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {user.email}
                {!user.isEmailVerified && ' · не подтверждён'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      key: "telegramId",
      header: "Telegram ID",
      sortable: true,
      hiddenOnMobile: true,
      render: (value) => (
        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
          {String(value)}
        </code>
      ),
    },
    {
      key: "phone",
      header: "Телефон",
      sortable: true,
      hiddenOnMobile: true,
      render: (value) => (value ? String(value) : "—"),
    },
    {
      key: "countryId",
      header: "Страна",
      width: "140px",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) => {
        if (!user.country) {
          // fallback на geoIP-страну, если выбранной нет
          if (user.lastCountry) {
            return (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <span>{codeToFlagEmoji(user.lastCountry)}</span>
                      <span className="uppercase text-xs">{user.lastCountry}</span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Определена по IP, не выбрана пользователем</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <span className="inline-flex items-center gap-1.5">
            <span>{codeToFlagEmoji(user.country.code)}</span>
            <span className="text-sm truncate">{user.country.name}</span>
          </span>
        );
      },
    },
    {
      key: "languagePreference",
      header: "Язык",
      width: "90px",
      sortable: true,
      render: (_, user) => {
        if (!user.languagePreference) {
          return <span className="text-muted-foreground">—</span>;
        }
        const lang = availableLanguages.find(
          (l) => l.code === user.languagePreference,
        );
        return (
          <span className="inline-flex items-center gap-1.5">
            <span>{getFlagEmoji(user.languagePreference)}</span>
            <span className="uppercase text-xs text-muted-foreground">
              {lang?.code ?? user.languagePreference}
            </span>
          </span>
        );
      },
    },
    {
      key: "currencyPreference",
      header: "Валюта",
      width: "80px",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) =>
        user.currencyPreference ? (
          <span className="text-xs font-medium uppercase">{user.currencyPreference}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "timezone",
      header: "Часовой пояс",
      width: "140px",
      hiddenOnMobile: true,
      render: (_, user) =>
        user.timezone ? (
          <span className="text-xs text-muted-foreground">{user.timezone}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "totalSpentBase",
      header: "Потрачено",
      width: "110px",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) => (
        <span className="tabular-nums">{formatMoney(user.totalSpentBase)}</span>
      ),
    },
    {
      key: "purchaseCount",
      header: "Покупок",
      width: "90px",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) => (
        <span className="tabular-nums">{user.purchaseCount ?? 0}</span>
      ),
    },
    {
      key: "lastPaymentAt",
      header: "Последняя оплата",
      width: "150px",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) =>
        user.lastPaymentAt ? (
          formatDate(user.lastPaymentAt)
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "acquisitionChannel",
      header: "Канал",
      width: "110px",
      sortable: true,
      hiddenOnMobile: true,
      render: (_, user) =>
        user.acquisitionChannel ? (
          <Badge variant="outline" className="text-xs">
            {ACQUISITION_LABELS[user.acquisitionChannel] ?? user.acquisitionChannel}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "utmSource",
      header: "UTM source",
      width: "130px",
      hiddenOnMobile: true,
      render: (_, user) =>
        user.utmSource ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs truncate max-w-[120px] inline-block align-middle">
                  {user.utmSource}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs whitespace-pre-wrap">
                {[
                  `source: ${user.utmSource}`,
                  user.utmMedium && `medium: ${user.utmMedium}`,
                  user.utmCampaign && `campaign: ${user.utmCampaign}`,
                  user.utmTerm && `term: ${user.utmTerm}`,
                  user.utmContent && `content: ${user.utmContent}`,
                ]
                  .filter(Boolean)
                  .join('\n')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: "subscriptions",
      header: "Подписка",
      render: (_, user) => getSubscriptionBadge(user.subscriptions[0]?.status),
    },
    {
      key: "tags",
      header: "Теги",
      hiddenOnMobile: true,
      render: (_, user) => <TagsBadges tags={user.tags} />,
    },
    {
      key: "blockedAt",
      header: "Статус",
      width: "140px",
      render: (_, user) => getAccountStatusBadge(user),
    },
    {
      key: "createdAt",
      header: "Регистрация",
      sortable: true,
      hiddenOnMobile: true,
      render: (value) => formatDate(String(value)),
    },
  ];

  const bulkActions: BulkAction<User>[] = [
    {
      label: "Просмотр",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/users/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Теги",
      icon: <Tag className="h-4 w-4" />,
      onClick: (items) => {
        setTagsDialogUserIds(items.map((u) => u.id));
        setTagsDialogLabel(
          items.length === 1
            ? `«${buildUserName(items[0])}»`
            : `${items.length} пользователей`,
        );
        setTagsDialogOpen(true);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Посмотреть логи",
      icon: <History className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/audit-logs?userId=${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Заблокировать",
      icon: <Lock className="h-4 w-4" />,
      variant: "destructive",
      onClick: (items) => {
        handleOpenBlock(items);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Разблокировать",
      icon: <Unlock className="h-4 w-4" />,
      onClick: (items) => {
        void handleUnblock(items);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      variant: "destructive",
      onClick: (items) => {
        void handleDelete(items);
      },
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Пользователи"
        description="Зарегистрированные пользователи бота"
        sticky
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <TableToolbar
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder="Поиск по имени, username или телефону..."
          />
        </div>
        {/* Все основные фильтры собраны в одну боковую панель — освобождаем место таблице */}
        <UsersFiltersSheet
          value={filtersValue}
          onChange={(next) => {
            setFiltersValue(next);
            setPage(1);
            setBulkAllIds(null);
          }}
          availableTags={availableTags}
          availableLanguages={availableLanguages}
          availableCountries={availableCountries}
          availableCurrencies={availableCurrencies}
          acquisitionOptions={acquisitionOptions}
        />

        {/* Расширенный фильтр — сегментный конструктор (общий компонент) */}
        <SegmentFilterSheet
          value={activeSegment}
          onChange={(next) => {
            setActiveSegment(next);
            setPage(1);
            setBulkAllIds(null);
          }}
          mode="apply"
          triggerLabel="Расширенный фильтр"
          title="Расширенный фильтр"
          onOpen={() => void loadSavedSegments()}
        >
          {({ current, setCurrent }) => (
            <>
              {/* Загрузка сохранённого сегмента */}
              <div className="space-y-2 mb-6">
                <p className="text-sm font-medium">Загрузить сохранённый сегмент</p>
                {isLoadingSegments ? (
                  <p className="text-sm text-muted-foreground">Загрузка...</p>
                ) : savedSegments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Нет сохранённых сегментов</p>
                ) : (
                  <Select
                    onValueChange={(value) => {
                      const found = savedSegments.find((s) => String(s.id) === value);
                      if (found) {
                        setCurrent(found.definition);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Выбрать сегмент..." />
                    </SelectTrigger>
                    <SelectContent>
                      {savedSegments.map((seg) => (
                        <SelectItem key={seg.id} value={String(seg.id)}>
                          {seg.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Сохранение текущего сегмента */}
              <div className="space-y-2 mb-6">
                <p className="text-sm font-medium">Сохранить как сегмент</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Название сегмента..."
                    value={saveSegmentName}
                    onChange={(e) => setSaveSegmentName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        void handleSaveSegment(current);
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => void handleSaveSegment(current)}
                    disabled={isSavingSegment || !saveSegmentName.trim()}
                  >
                    {isSavingSegment ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SegmentFilterSheet>

        {/* «Выбрать всё по фильтру» — загружает все userId с backend */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleSelectAllByFilter()}
          disabled={isLoadingAllIds}
          className="gap-2"
        >
          <CheckSquare className="h-4 w-4" />
          {isLoadingAllIds ? 'Загрузка...' : 'Выбрать всё по фильтру'}
        </Button>

        {/* Bulk-действия для всего фильтра (если выбраны все) */}
        {bulkAllIds !== null && bulkAllIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Выбрано {bulkAllIds.length}:
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTagsByFilter}
              className="gap-1"
            >
              <Tag className="h-3.5 w-3.5" />
              Теги
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkBlockByFilter}
              className="gap-1"
            >
              <Lock className="h-3.5 w-3.5" />
              Заблокировать
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setBulkAllIds(null)}
              className="text-muted-foreground"
            >
              Отменить выборку
            </Button>
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(user) => go({ to: `/users/show/${user.id}` })}
        emptyMessage="Нет пользователей"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <BlockUserDialog
        open={blockDialogOpen}
        onOpenChange={(open) => {
          setBlockDialogOpen(open);
          if (!open) {
            setBlockTargets([]);
          }
        }}
        onConfirm={handleConfirmBlock}
        userName={
          blockTargets.length === 1
            ? `«${buildUserName(blockTargets[0])}»`
            : `${blockTargets.length} пользователей`
        }
      />

      <BulkTagsDialog
        open={tagsDialogOpen}
        onOpenChange={(open) => {
          setTagsDialogOpen(open);
          if (!open) {
            setTagsDialogUserIds([]);
            setTagsDialogLabel('');
          }
        }}
        userIds={tagsDialogUserIds}
        selectionLabel={tagsDialogLabel}
        onSuccess={() => void listResult.query.refetch()}
      />

      {ConfirmationDialog}
    </div>
  );
}
